"use client";
import { useEffect, useState, useMemo, useId } from "react";
import { usePathname } from "@/i18n/navigation";
import axios from "axios";
import Loader from "../Loader/Loader";
import styles from "./speedTest.module.css";

const GAUGE_MAX = 1000;
const GAUGE_ANGLE_SPAN = 180; // semi-circle: 0° at left, 1000 at right

/** Browser-based download speed test using fetch (bytes / time → Mbps). */
async function measureDownloadSpeedMbps(): Promise<number> {
  const url = "https://eu.httpbin.org/stream-bytes/500000";
  const sizeBytes = 500_000;
  const start = performance.now();
  const res = await fetch(url, { mode: "cors" });
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const durationSec = (performance.now() - start) / 1000;
  const bits = (blob.size || sizeBytes) * 8;
  const mbps = bits / 1e6 / (durationSec || 0.001);
  return Math.max(0, mbps);
}

/** Rough latency (ms) via small request time-to-first-byte. */
async function measurePingMs(): Promise<number> {
  const url = "https://ipapi.co/json/";
  const start = performance.now();
  await fetch(url, { mode: "cors" });
  return Math.round(performance.now() - start);
}

function ProxyFormat() {
  const pathname = usePathname();
  const IP = require("what-is-my-ip-address");
  const [iPAddress, setIPAddress] = useState("");
  const [download, setDownload] = useState("");
  const [pingMs, setPingMs] = useState<number | null>(null);
  const [geoLocation, setGeoLocation] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [speedTestPhase, setSpeedTestPhase] = useState<"idle" | "testing" | "results">("idle");

  useEffect(() => {
    setIPAddress("");
    setDownload("");
    setGeoLocation({});
  }, []);

  const handleGeolocationData = () => {
    setLoading(true);
    axios.get(`https://ipapi.co/json/`).then((response) => {
      setGeoLocation(response.data);
      setLoading(false);
    });
  };

  const handleIPAddress = () => {
    setLoading(true);
    IP.v4().then((res: string) => {
      setIPAddress(res);
      setLoading(false);
    });
  };

  async function runSpeedTest() {
    if (!pathname?.includes("check-internet-speed-test")) return;
    setSpeedTestPhase("testing");
    setErrorMessage(null);
    setDownload("");
    setPingMs(null);
    try {
      const geoPromise = axios.get("https://ipapi.co/json/").then((r) => r.data).catch(() => ({}));
      const pingPromise = measurePingMs();
      let downloadMbps = 0;
      try {
        downloadMbps = await measureDownloadSpeedMbps();
      } catch (e) {
        setErrorMessage("Download test failed. Check your connection or try again.");
      }
      const [geoRes, ping] = await Promise.all([geoPromise, pingPromise]);
      setDownload(String(downloadMbps.toFixed(2)));
      setPingMs(ping);
      setGeoLocation(geoRes);
      setSpeedTestPhase("results");
    } catch {
      setErrorMessage("Speed test failed. Please try again.");
      setDownload("0");
      setSpeedTestPhase("results");
    }
  }

  const downloadNum = useMemo(() => Math.min(GAUGE_MAX, Math.max(0, Number(download) || 0)), [download]);
  const needleAngle = useMemo(() => {
    const p = downloadNum / GAUGE_MAX;
    return -90 + p * GAUGE_ANGLE_SPAN; // -90° (left) clockwise to 90° (right)
  }, [downloadNum]);

  const isSpeedTestPage = pathname?.includes("check-internet-speed-test") ?? false;
  const gaugeGradientId = useId();

  if (isSpeedTestPage) {
    return (
      <div className={styles.speedTestPage}>
        {speedTestPhase === "idle" && (
          <div className={styles.goWrapper}>
            <button
              type="button"
              className={styles.goButton}
              onClick={runSpeedTest}
              aria-label="Start speed test"
            >
              <span className={styles.goLabel}>GO</span>
            </button>
          </div>
        )}

        {speedTestPhase === "testing" && (
          <div className={styles.goWrapper}>
            <div className={`${styles.goButton} ${styles.goButtonBusy}`} aria-busy="true">
              <Loader />
            </div>
            <p className={styles.testingLabel}>Testing download speed…</p>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} />
            </div>
          </div>
        )}

        {speedTestPhase === "results" && (
          <div className={styles.resultsWrapper}>
            {errorMessage && (
              <div className={styles.errorBanner} role="alert">
                {errorMessage}
              </div>
            )}
            <div className={styles.gaugeContainer}>
              <SpeedometerGauge
                value={downloadNum}
                needleAngle={needleAngle}
                gradientId={gaugeGradientId}
              />
              <div className={styles.speedValue}>
                <div className={styles.speedNumber}>{Number(download).toFixed(2)}</div>
                <div className={styles.speedUnit}>
                  <DownloadArrowIcon />
                  Mbps
                </div>
              </div>
            </div>
            {pingMs != null && (
              <div className={styles.pingRow}>
                <span className={styles.pingLabel}>Latency</span>
                <span className={styles.pingValue}>{pingMs} ms</span>
              </div>
            )}
            <div className={styles.connectionDetails}>
              <div className={styles.connectionCard}>
                <div className={styles.connectionLabel}>
                  <UserIcon />
                  Server
                </div>
                <span className={styles.connectionValue}>Speed Test</span>
                <span className={styles.connectionSub}>{String(geoLocation?.ip ?? "—")}</span>
              </div>
              <div className={styles.connectionCard}>
                <div className={styles.connectionLabel}>
                  <GlobeIcon />
                  ISP · Location
                </div>
                <span className={styles.connectionValue}>
                  {String(geoLocation?.org ?? "—")}
                </span>
                <span className={styles.connectionSub}>{String(geoLocation?.city ?? "—")}</span>
              </div>
            </div>
            <button
              type="button"
              className={styles.runAgain}
              onClick={() => {
                setSpeedTestPhase("idle");
                setDownload("");
                setErrorMessage(null);
                setPingMs(null);
              }}
            >
              Run again
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="proxyContainer">
      <div className="container">
        <div className="content-box">
          <div className="flex-box">
            <div className="column-box">
              {pathname?.includes("/check-ip-address") && (
                <>
                  {iPAddress && (
                    <div>
                      This is your IP: <b>{iPAddress}</b>
                    </div>
                  )}
                  <div className="button lowercase-center-alignment">
                    <button onClick={handleIPAddress}>
                      What is my IP?
                      {loading && <Loader />}
                    </button>
                  </div>
                </>
              )}

              {pathname?.includes("/find-ip-location") && (
                <>
                  {Object.keys(geoLocation).length > 0 && (
                    <>
                      <div>This is your Location</div>
                      <div>
                        Location:{" "}
                        <b>
                          {String(geoLocation?.city ?? "")}, {String(geoLocation?.region ?? "")},{" "}
                          {String(geoLocation?.country_name ?? "")}
                        </b>
                      </div>
                      <div>
                        Latitude: <b>{String(geoLocation?.latitude ?? "")}</b>
                      </div>
                      <div>
                        Longitude: <b>{String(geoLocation?.longitude ?? "")}</b>
                      </div>
                      <div>
                        Timezone: <b>{String(geoLocation?.timezone ?? "")}</b>
                      </div>
                    </>
                  )}
                  <div className="button lowercase-center-alignment">
                    <button onClick={handleGeolocationData}>
                      What is my Location?
                      {loading && <Loader />}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpeedometerGauge({
  value,
  needleAngle,
  gradientId,
}: {
  value: number;
  needleAngle: number;
  gradientId: string;
}) {
  const r = 100;
  const cx = 140;
  const cy = 140;
  const startAngle = -90; // left
  const endAngle = 90; // right – semi-circle (180°)
  const angleSpan = 180;
  const fillRatio = Math.min(1, Math.max(0, value / GAUGE_MAX));
  const strokeWidth = 12;
  const pathLength = 1;
  const needleLength = r - strokeWidth;

  const polarToCartesian = (angle: number, radius: number) => {
    const rad = (angle * Math.PI) / 180;
    return {
      x: cx + radius * Math.cos(rad),
      y: cy + radius * Math.sin(rad),
    };
  };

  const describeArc = (start: number, end: number) => {
    const startPt = polarToCartesian(start, r);
    const endPt = polarToCartesian(end, r);
    const large = end - start > 180 ? 1 : 0;
    return `M ${startPt.x} ${startPt.y} A ${r} ${r} 0 ${large} 1 ${endPt.x} ${endPt.y}`;
  };

  const trackArc = describeArc(startAngle, endAngle);
  const ticks = [0, 100, 250, 500, 750, 1000];
  const labelR = r + 22;

  return (
    <svg
      className={styles.gaugeSvg}
      viewBox="0 0 280 280"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#06b6d4" />
          <stop offset="100%" stopColor="#14b8a6" />
        </linearGradient>
      </defs>
      {/* Background arc – semi-circle track */}
      <path
        d={trackArc}
        fill="none"
        stroke="rgba(30, 58, 138, 0.85)"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      {/* Filled arc – value portion */}
      <path
        d={trackArc}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        pathLength={pathLength}
        strokeDasharray={`${fillRatio} ${pathLength - fillRatio}`}
        strokeDashoffset={0}
      />
      {/* Tick labels – spread along 180° arc */}
      {ticks.map((t) => {
        const angle = -90 + (t / GAUGE_MAX) * angleSpan;
        const pt = polarToCartesian(angle, labelR);
        return (
          <text
            key={t}
            x={pt.x}
            y={pt.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="rgba(255,255,255,0.92)"
            fontSize="13"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontWeight="600"
            className={styles.tabularNums}
          >
            {t}
          </text>
        );
      })}
      {/* Needle – from center to tip at needleAngle */}
      <line
        x1={cx}
        y1={cy}
        x2={polarToCartesian(needleAngle, needleLength).x}
        y2={polarToCartesian(needleAngle, needleLength).y}
        stroke="rgba(255,255,255,0.95)"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function DownloadArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 5v14M5 12l7 7 7-7" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="8" r="3" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

export default ProxyFormat;
